import React from 'react';
import { CaptureFeedback, CaptureSequence } from './types';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowRight, Check, CheckCircle2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { POSTURE_THEME } from './lib/theme';

export type WizardMessageOverrides = Partial<Record<CaptureSequence, { title?: string; instruction?: string }>>;

interface GuidedCaptureWizardProps {
  currentStep: number;
  totalSteps: number;
  currentView: CaptureSequence;
  onStepChange: (step: number) => void;
  onComplete: () => void;
  isCaptureComplete: boolean;
  /** Full-screen mobile capture: tighter type, clamped instruction, no wait-for-AI spinner. */
  immersiveMobile?: boolean;
  /** Override default step titles and instructions (e.g. plain-language copy for self-assessment). */
  messageOverrides?: WizardMessageOverrides;
  /**
   * S3b: live capture coaching, docked here instead of floating over the frame. While present it
   * takes the instruction line — the coaching is more urgent than a static instruction the user has
   * already read, and the step title stays put so context is not lost.
   */
  feedback?: CaptureFeedback | null;
  /** Renders the camera close control at the end of the bar (it used to float over the frame). */
  onCloseCamera?: () => void;
}

const WIZARD_MESSAGES: Record<CaptureSequence, { title: string; instruction: string }> = {
  Anterior: {
    title: "Front View",
    instruction: "Face the camera directly. Feet shoulder-width apart."
  },
  Right: {
    title: "Right Side",
    instruction: "Right shoulder to the camera."
  },
  Posterior: {
    title: "Back View",
    instruction: "Back to the camera."
  },
  Left: {
    title: "Left Side",
    instruction: "Left shoulder to the camera."
  }
};

/**
 * Instruction copy sits on a fixed dark scrim, so its colour must be stated on-dark rather than
 * resolved from the page theme: `text-muted-foreground` rendered dark grey on near-black wherever
 * the tree forces `light` (self-assessment). Exported so tests assert against one source of truth.
 */
export const WIZARD_INSTRUCTION_TEXT_CLASS = 'text-white/85';

/** Plain-language overrides used when selfAssessmentMode is active. */
export const SELF_ASSESSMENT_WIZARD_OVERRIDES: WizardMessageOverrides = {
  Anterior: {
    title: 'Front side',
    instruction: 'Stand naturally facing the camera. Keep your feet about shoulder-width apart and look straight ahead.',
  },
  Left: {
    title: 'Left side',
    instruction: 'Turn so your left shoulder faces the camera. Stand tall and breathe normally.',
  },
  Right: {
    title: 'Right side',
    instruction: 'Turn so your right shoulder faces the camera. Stand tall and breathe normally.',
  },
};

/**
 * Docked guided-capture info bar (S3b).
 *
 * This used to be a ~120 px stacked card at `absolute top-4 left-4 right-4` — directly over the
 * subject's head, which is the region carrying the most reliable lateralization signal we have. It
 * is now an in-flow row above the media area: hairline progress, step counter, title, one line of
 * instruction (or live coaching), and the close button that used to float separately at `z-[60]`.
 *
 * Two things deliberately left the persistent bar rather than being shrunk:
 * - the step-1 setup reminder — it is advice about where to put the phone, so once the camera is
 *   live it is too late to act on; `CaptureModule` shows it in the pre-camera state instead;
 * - the "tap the shutter below" hint — it existed because the shutter was hard to find inside a
 *   floating pill, and a docked strip makes it redundant.
 */
const GuidedCaptureWizard: React.FC<GuidedCaptureWizardProps> = ({
  currentStep,
  totalSteps,
  currentView,
  onStepChange,
  onComplete,
  isCaptureComplete,
  immersiveMobile = false,
  messageOverrides,
  feedback = null,
  onCloseCamera,
}) => {
  const base = WIZARD_MESSAGES[currentView];
  const overrides = messageOverrides?.[currentView];
  const info = {
    title: overrides?.title ?? base.title,
    instruction: overrides?.instruction ?? base.instruction,
  };

  const feedbackTone = feedback
    ? {
        success: POSTURE_THEME.colors.good,
        warning: POSTURE_THEME.colors.warning,
        error: POSTURE_THEME.colors.critical,
        info: POSTURE_THEME.colors.neutral,
      }[feedback.type]
    : null;

  return (
    <div
      data-testid="capture-info-bar"
      className={cn(
        'relative z-20 flex shrink-0 flex-col gap-1 border-b border-white/10 bg-black/70 backdrop-blur-md',
        'px-2 pb-1.5 pt-[max(0.375rem,env(safe-area-inset-top))]'
      )}
    >
      {/* Progress — a hairline now; it never needed to be a 4 px bar with its own row. */}
      <div className="h-0.5 w-full overflow-hidden rounded-full bg-white/20">
        <div
          className="h-full bg-cyan-400 transition-all duration-500 ease-out"
          style={{ width: `${((currentStep + (isCaptureComplete ? 1 : 0)) / totalSteps) * 100}%` }}
        />
      </div>

      <div className="flex items-center gap-2">
        <span className="shrink-0 rounded-full bg-cyan-400/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-300">
          <span className="sr-only">Step </span>
          {currentStep + 1}
          <span aria-hidden="true">/</span>
          <span className="sr-only"> of </span>
          {totalSteps}
        </span>

        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <h3 className={cn('truncate font-bold text-white', immersiveMobile ? 'text-xs' : 'text-sm')}>
            {info.title}
          </h3>
          {feedback && feedbackTone ? (
            /*
              S4/U4 semantics preserved verbatim from the floating panel: errors interrupt, the
              running commentary waits for a pause so it is not overwhelming.
            */
            <p
              role={feedback.type === 'error' ? 'alert' : 'status'}
              aria-live={feedback.type === 'error' ? 'assertive' : 'polite'}
              data-testid="capture-live-feedback"
              className={cn('flex items-center gap-1 truncate font-medium', feedbackTone.text, immersiveMobile ? 'text-[11px]' : 'text-xs')}
            >
              {feedback.type === 'success' ? (
                <CheckCircle2 className="h-3 w-3 shrink-0" aria-hidden="true" />
              ) : (
                <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
              )}
              <span className="truncate">{feedback.message}</span>
            </p>
          ) : (
            <p
              className={cn(
                WIZARD_INSTRUCTION_TEXT_CLASS,
                immersiveMobile ? 'text-[11px] line-clamp-2' : 'truncate text-xs'
              )}
            >
              {info.instruction}
            </p>
          )}
        </div>

        {isCaptureComplete && (
          <Button
            size="icon"
            className="h-11 w-11 shrink-0 rounded-full bg-cyan-500 text-white hover:bg-cyan-600"
            aria-label={currentStep < totalSteps - 1 ? 'Next view' : 'Finish capture'}
            onClick={() => {
              if (currentStep < totalSteps - 1) {
                onStepChange(currentStep + 1);
              } else {
                onComplete();
              }
            }}
          >
            {currentStep < totalSteps - 1 ? <ArrowRight className="h-5 w-5" /> : <Check className="h-5 w-5" />}
          </Button>
        )}

        {!isCaptureComplete && !immersiveMobile && (
          <div className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-white/20 border-t-cyan-400" />
        )}

        {onCloseCamera && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-11 w-11 shrink-0 rounded-full text-white hover:bg-white/20"
            aria-label="Close camera"
            title="Close camera"
            onClick={onCloseCamera}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default GuidedCaptureWizard;
